import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  spacing,
  mediaMax,
  zIndex,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray02,
  gray04,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import Link from 'presentations/views/components/atoms/Link';
import BlogContent from 'presentations/views/components/organisms/BlogContent';
import RelatedFeatureListItem from 'presentations/views/components/molecules/RelatedFeatureListItem';
import Headline from 'presentations/views/components/atoms/Headline';
import BlogPageTemplate from 'presentations/views/components/templates/ResourcePageTemplate';
import { useBlogPage } from './hooks';
import BlogShareList from 'presentations/views/components/organisms/ResourceShareList';
import Avatar from 'presentations/views/components/atoms/Avatar';
import { formatToDate } from 'infras/datetime';

import dynamic from 'next/dynamic';
import { BlogItem } from 'presentations/api/schema/contents';
import { css } from '@emotion/react';
import { newsPath } from 'presentations/routes/path';
import { ReactNode } from 'react';
const Breadcrumbs = dynamic(() =>
  import('presentations/views/components/organisms/Breadcrumbs'),
);

interface Props {
  blog: BlogItem;
  populars: BlogItem[];
  siderbar?: ReactNode;
  header?: ReactNode;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useBlogPage({
    blog: props.blog,
    populars: props.populars,
  });
  const isShowAd = props.blog.category !== 'engineering' && !state.viewer;
  const { isUnlisted } = props.blog;
  const isShowPodcast = !!props.blog.podcastUrl;

  const renderContentHeader = () => {
    return (
      <ContentHeader isFullWidth={!isShowPodcast}>
        <HeaderImageContainer isFullWidth={!isShowPodcast}>
          {props.blog.thumbnailUrl && (
            <HeaderImage
              src={props.blog.thumbnailUrl}
              alt={props.blog.title}
              width={672}
              height={448}
            />
          )}
        </HeaderImageContainer>
        <TitleContainer isFullWidth={!isShowPodcast}>
          <ContentCategoryLink href={newsPath(props.blog.slug)}>
            {t(`category.${props.blog.category}`)}
          </ContentCategoryLink>
          <Title>{props.blog.title}</Title>
          <CreatedAt>{formatToDate(props.blog.updatedAt)}</CreatedAt>
          {props.blog.author && (
            <AuthorContainer>
              {props.blog.author.profileImage && (
                <AuthorAvatar
                  src={props.blog.author.profileImage}
                  alt={props.blog.author.name}
                />
              )}
              <AuthorName>{props.blog.author.name}</AuthorName>
            </AuthorContainer>
          )}
          {props.blog.podcastTag && (
            <PodcastContainer>
              <div
                dangerouslySetInnerHTML={{ __html: props.blog.podcastTag }}
              />
            </PodcastContainer>
          )}
        </TitleContainer>
      </ContentHeader>
    );
  };

  return (
    <BlogPageTemplate
      breadcrumbs={!isUnlisted && <Breadcrumbs routes={state.routes} />}
      header={props.header}
    >
      <>
        {isShowPodcast && renderContentHeader()}
        <Container>
          <MainContent>
            {!isShowPodcast && renderContentHeader()}
            <BlogContent
              blog={props.blog}
              productType={state.productType}
              isShowAd={isShowAd}
              shareTitle={props.blog.title}
              shareUrl={state.shareUrl}
            />
            {!isUnlisted && (
              <ShareContainer>
                <ShareTitle>{t('share')}</ShareTitle>
                <ShareIconContainer>
                  <BlogShareList
                    shareTitle={props.blog.title}
                    shareUrl={state.shareUrl}
                    direction="row"
                  />
                </ShareIconContainer>
              </ShareContainer>
            )}
            {props.blog.relatedFeatures.length > 0 && (
              <RecommendContainer>
                <RecommendTitle variant="h2">
                  {t('related features')}
                </RecommendTitle>
                {props.blog.relatedFeatures.map((feature) => (
                  <RecommendListItemContainer key={feature.id}>
                    <RelatedFeatureListItem feature={feature} />
                  </RecommendListItemContainer>
                ))}
              </RecommendContainer>
            )}
          </MainContent>
          <SubContent>
            <SidebarContainer>{props.siderbar}</SidebarContainer>
          </SubContent>
        </Container>
      </>
    </BlogPageTemplate>
  );
}

const Container = styled.div`
  letter-spacing: 0.05em;
  display: flex;
  justify-content: center;
  padding: ${spacing(48)} 0;
  ${mediaMax.desktop} {
    display: block;
  }
  ${mediaMax.tablet} {
    padding: ${spacing(16)} 0;
  }
`;
const MainContent = styled.div`
  width: 672px;
  ${mediaMax.desktop} {
    margin: 0 auto;
  }
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(12)};
    margin: 0;
  }
`;
const SidebarContainer = styled.div``;
const SubContent = styled.div`
  width: 276px;
  margin-left: ${spacing(40)};
  ${mediaMax.desktop} {
    width: 672px;
    margin: 0 auto;
    margin-top: ${spacing(24)};
  }
  ${mediaMax.tablet} {
    margin-left: 0;
    width: 100%;
    padding: 0 ${spacing(12)};
  }
`;

const AdContainer = styled.div`
  margin-bottom: ${spacing(48)};
`;
const ShareContainer = styled.div`
  margin: 0 0 ${spacing(72)} 0;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(40)} 0;
  }
`;

const ShareTitle = styled.div`
  margin: 0 0 ${spacing(20)} 0;

  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
  line-height: 150%;
  text-align: center;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(24)} 0;
    font-size: ${fontSize(13)};
  }
`;

const ShareIconContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const RecommendContainer = styled.div`
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;

const RecommendTitle = styled(Headline)`
  margin-bottom: ${spacing(32)};

  font-weight: ${fontWeight('bold')};
  line-height: 30px;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(20)} 0;
    padding: 0 0 ${spacing(12)} 0;
    border-bottom: 1px solid ${gray01};
  }
`;

const RecommendListItemContainer = styled.div`
  padding-bottom: ${spacing(32)};
  margin-bottom: ${spacing(32)};
  border-bottom: 1px solid ${gray04};
  :last-of-type {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;

export const AdFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(1100)};
`;

const HeaderImageContainer = styled.div<{ isFullWidth: boolean }>`
  ${(props) =>
    props.isFullWidth
      ? css`
          width: 672px;
          /* height: 448px; */
        `
      : css`
          width: 400px;
          height: auto;
          margin-right: ${spacing(32)};
        `};
  position: relative;

  ${mediaMax.tablet} {
    width: 100%;
    height: auto;
    min-height: 198px;
    margin: ${spacing(20)} 0;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${borderRadius()};
`;

const Title = styled.h1`
  font-size: ${fontSize(28)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.4;
  letter-spacing: 0.05em;
  overflow-wrap: break-word;

  ${mediaMax.tablet} {
    font-size: ${fontSize(20)};
  }
`;

const AuthorContainer = styled.div`
  display: flex;
  margin-top: ${spacing(8)};
`;

const AuthorAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;
  margin-right: ${spacing(8)};

  ${mediaMax.tablet} {
    margin-right: ${spacing(6)};
    width: 30px;
    height: 30px;
  }
`;

const AuthorName = styled.span`
  display: flex;
  align-items: center;

  font-size: ${fontSize(14)};
  line-height: 160%;
  color: ${gray02};

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;

const CreatedAt = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-top: ${spacing(8)};

  font-size: ${fontSize(14)};
  line-height: 160%;
  color: ${gray02};

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;

const TitleContainer = styled.div<{ isFullWidth: boolean }>`
  flex: 1;
  ${(props) =>
    props.isFullWidth &&
    css`
      margin-top: ${spacing(32)};
      ${mediaMax.tablet} {
        margin-top: ${spacing(20)};
      }
    `}
`;
const PodcastContainer = styled.div`
  margin-top: ${spacing(24)};
  ${mediaMax.tablet} {
    iframe {
      width: 100%;
    }
  }
`;
const ContentHeader = styled.div<{ isFullWidth: boolean }>`
  display: flex;
  max-width: 988px;
  margin: 0 auto;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)};
  }
  ${(props) =>
    props.isFullWidth
      ? css`
          display: block;
        `
      : css`
          justify-content: space-between;
          margin-top: ${spacing(48)};
          ${mediaMax.tablet} {
            display: block;
          }
        `}
`;

const ContentCategoryLink = styled(Link)`
  display: inline-block;
  font-weight: ${fontWeight('bold')};
  color: ${whiteFix};
  background-color: ${primary};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};
  padding: 0 ${spacing(8)};
  border-radius: ${borderRadius(4)};
  margin-bottom: ${spacing(8)};
`;
